<template>
  <!-- EmailModal se teleporta y controla su z-index -->
  <teleport to="body">
    <div v-if="visible" :style="overlayStyle" class="email-modal-teleport-wrapper" role="dialog" aria-modal="true">
      <div :style="containerStyle" class="email-modal-container">
        <div class="modal-header">
          <h3 class="modal-title">{{ titleLabel }}</h3>
          <button type="button" class="btn-close" @click="close" aria-label="Cerrar" title="Cerrar">
            <span class="material-icons action-icon" aria-hidden="true">close</span>
          </button>
        </div>

        <div class="modal-body">
          <p style="color:#6c757d; margin-bottom:12px;">El correo se enviará automáticamente al creador del ticket.</p>

          <div v-if="!viewOnly">
            <div class="form-group">
              <label>{{ $t('asunto') }}</label>
              <input v-model="subject" type="text" />
            </div>

            <div class="form-group">
              <label>{{ $t('mensaje') }}</label>
              <textarea v-model="message" rows="6" :maxlength="emailMaxLength"></textarea>
              <div style="text-align:right; margin-top:6px; font-size:0.85rem; color:#666;">
                {{ remainingCharsMessage }} {{ $t('caracteres_restantes') }}
              </div>
            </div>
          </div>

          <!-- Lista de emails enviados relacionados con este ticket -->
          <div class="form-group" v-if="visible">
            <label>{{ $t('emails_enviados') }}</label>
            <div style="max-height:180px; overflow:auto; border:1px solid #eee; padding:8px; background:#fafafa;">
              <div v-if="loadingEmails" style="color:#666">{{ $t('cargando') }}</div>
              <div v-else-if="!sentEmails || sentEmails.length === 0" style="color:#666">{{ $t('no_correos_enviados') }}</div>
              <ul v-else style="padding-left:0; list-style:none; margin:0;">
                <li v-for="email in sentEmails" :key="email.id" style="border-bottom:1px solid #eee; padding:8px 0;">
                  <!-- Mostrar destinatario justo encima del asunto ("Para:" en negrita) -->
                  <div style="font-size:0.85rem; color:#666; margin-bottom:4px;"><strong>{{ $t('para_label') }}</strong> {{ email.destinatario?.nombre_completo || email.destinatario?.email || email.to_user_id || $t('desconocido') }}</div>
                  <div style="display:flex; justify-content:space-between; align-items:baseline; gap:8px;">
                    <div style="font-size:0.95rem; color:#333; font-weight:600;">{{ email.subject || '(sin asunto)' }}</div>
                    <div style="font-size:0.8rem; color:#666;">{{ formatSentAt(email.sent_at || email.created_at || email.sentAt) }}</div>
                  </div>
                  <div class="email-body" style="margin-top:6px; color:#333; font-size:0.95rem;">
                    <template v-if="expandedEmailIds[email.id]">
                      <div class="email-body-full">
                        {{ email.body }}
                      </div>
                      <a href="#" @click.prevent="toggleEmailExpand(email.id)" class="email-toggle">ver menos...</a>
                    </template>
                    <template v-else>
                      <div class="email-body-preview">
                        <!-- si hay segunda línea, mostrar dos filas y poner el enlace inline en la segunda -->
                        <!-- si hay tercera línea, mostrar 3 filas y poner el enlace inline en la tercera -->
                        <template v-if="previewLines(email.body)[2]">
                          <div class="preview-line preview-line-1">{{ previewLines(email.body)[0] }}</div>
                          <div class="preview-line preview-line-2">{{ previewLines(email.body)[1] }}</div>
                          <div class="preview-line preview-line-3">
                            <span class="preview-line-3-text">{{ previewLines(email.body)[2] }}</span>
                            <a v-if="hasMoreLines(email.body)" href="#" @click.prevent="toggleEmailExpand(email.id)" class="email-toggle">ver más...</a>
                          </div>
                        </template>
                        <!-- si no hay tercera línea pero sí hay segunda, mostrar 2 filas y poner el enlace inline en la segunda -->
                        <template v-else-if="previewLines(email.body)[1]">
                          <div class="preview-line preview-line-1">{{ previewLines(email.body)[0] }}</div>
                          <div class="preview-line preview-line-2">
                            <span class="preview-line-2-text">{{ previewLines(email.body)[1] }}</span>
                            <a v-if="hasMoreLines(email.body)" href="#" @click.prevent="toggleEmailExpand(email.id)" class="email-toggle">ver más...</a>
                          </div>
                        </template>
                        <!-- si sólo hay una línea, truncar esa única línea y poner el enlace inline -->
                        <template v-else>
                          <div class="preview-line preview-line-single">
                            <span class="preview-line-single-text">{{ previewLines(email.body)[0] }}</span>
                            <a v-if="hasMoreLines(email.body)" href="#" @click.prevent="toggleEmailExpand(email.id)" class="email-toggle">ver más...</a>
                          </div>
                        </template>
                      </div>
                    </template>
                  </div>
                </li>
              </ul>
            </div>
            <div style="margin-top:8px; display:flex; justify-content:flex-end; align-items:center; gap:12px;">
              <div style="font-size:0.85rem; color:#666">
                Página {{ emailsPagination.current_page }} de {{ emailsPagination.last_page }} — {{ emailsPagination.total }} correo(s)
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">{{ $t('cerrar') }}</button>
          <button v-if="!viewOnly" type="button" class="btn btn-primary" @click="send" :disabled="sending || (message && message.length > emailMaxLength)">
            <span v-if="sending">{{ $t('enviando') }}</span>
            <span v-else>{{ $t('enviar') }}</span>
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import api from '@/api';
import Swal from 'sweetalert2';
import { formatDateTime } from '@/utils/formatDate';
export default {
  name: 'EmailModal',
  props: {
    zIndex: { type: Number, default: 140000 },
    visible: { type: Boolean, default: true },
    ticketId: { type: [String, Number], default: null },
    // opcional: pasar directamente el id del usuario creador para evitar fetch
    ticketCreatorId: { type: [String, Number], default: null },
    // aceptar el objeto ticket completo (cuando el padre pasa :ticket="ticket")
    ticket: { type: Object, default: null },
    // Si true, el modal se usa solo para visualización (no permitir enviar)
    viewOnly: { type: Boolean, default: false }
  },
  data() {
    return {
      to_user_id: this.ticketCreatorId || null,
      subject: this.ticketId ? `Información ticket ${this.ticketId}` : '',
      message: '',
      // máximo de caracteres para el cuerpo del email
      emailMaxLength: 1500,
      sending: false,
      loadingCreator: false
      ,
      // emails enviados relacionados con el ticket
      sentEmails: [],
      loadingEmails: false,
      expandedEmailIds: {},
      showAllEmails: false,
      // paginación
      emailsPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 5,
        total: 0,
      },
    };
  },
  computed: {
    // determinar id del ticket usando prop ticketId o el objeto ticket si se pasó
    effectiveTicketId() {
      if (this.ticketId) return this.ticketId;
      const t = this.ticket || {};
      return t.id || t.ticket?.id || t.data?.ticket?.id || null;
    },
    overlayStyle() {
      return {
        position: 'fixed',
        inset: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.80)', // más opaco para cubrir fondo
        zIndex: String(this.zIndex),
      };
    },
    containerStyle() {
      return {
        position: 'relative',
        zIndex: String(this.zIndex + 1),
      };
    },
    titleLabel() {
      return this.viewOnly ? (this.$t('emails_enviados') || 'Emails enviados') : (this.$t('enviar_correo') || 'Enviar correo');
    },
    remainingCharsMessage() {
      const len = this.message ? this.message.length : 0;
      return Math.max(0, this.emailMaxLength - len);
    },
  },
  watch: {
    // si el prop ticketCreatorId cambia, actualizar destinatario
    ticketCreatorId(val) {
      this.to_user_id = val || null;
    },
    // cuando se abra el modal y no tenemos creatorId, intentar cargarlo desde ticket
    visible(val) {
      if (val && !this.to_user_id && this.effectiveTicketId) {
        this.loadCreatorFromTicket();
      }
      // cuando el modal se abre, cargar los emails enviados
      if (val && this.effectiveTicketId) {
        // cargar una vista reducida por defecto (ej. últimos 5)
        this.loadSentEmails(5);
      }
    },
    // si recibimos el objeto ticket desde el padre y aún no tenemos destinatario, intentar extraerlo
    ticket: {
      immediate: true,
      handler(t) {
        if (!this.to_user_id && t) {
          const candidate = t.usuario_id || t.usuario?.id || t.ticket?.usuario_id || t.data?.usuario_id || null;
          if (candidate) this.to_user_id = candidate;
        }
        // cuando el ticket cambia, recargar la lista de emails si el componente está montado
        if (t && this.effectiveTicketId) {
          try { this.loadSentEmails(5); } catch (e) { /* ignore */ }
        }
      }
    }
  },

  methods: {
    async loadCreatorFromTicket() {
      const id = this.effectiveTicketId;
      if (!id) return;
       this.loadingCreator = true;
       try {
        const resp = await api.get(`/tickets/${id}`);
         const data = resp?.data ?? null;
         const candidato =
           (data && data.usuario_id) ||
           (data && data.data && data.data.usuario_id) ||
           (data && data.ticket && data.ticket.usuario_id) ||
           (data && data.usuario && (data.usuario.id || data.usuario_id)) ||
           null;
         if (candidato) this.to_user_id = candidato;
       } catch (e) {
         if (import.meta.env.DEV) console.error('No se pudo obtener creador del ticket:', e);
         Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo obtener el creador del ticket.', showConfirmButton: false, timer: 2500 });
       } finally {
         this.loadingCreator = false;
       }
     },

     async send() {
       // asegurar destinatario
      if (!this.to_user_id) {
        // intentar extraer del objeto ticket pasado desde el padre
        const t = this.ticket || {};
        const fallback = t.usuario_id || t.usuario?.id || t.ticket?.usuario_id || t.data?.usuario_id || null;
        if (fallback) this.to_user_id = fallback;
      }
      if (!this.to_user_id) {
        // intentar cargar por API si tenemos id efectivo
        if (this.effectiveTicketId) await this.loadCreatorFromTicket();
        if (!this.to_user_id) {
          Swal.fire({ icon: 'error', title: 'Error', text: 'No se puede determinar el creador del ticket.', showConfirmButton: false, timer: 2500 });
          return;
        }
      }
 
      // asegurarse de tener un id efectivo antes de llamar a la API
      const id = this.effectiveTicketId;
      if (!id) {
        if (import.meta.env.DEV) console.error('ticketId missing');
        return;
      }

       const payload = {
         to_user_id: this.to_user_id,
         subject: this.subject,
         body: this.message
       };
      this.sending = true;
      try {
        const resp = await api.post(`/tickets/${id}/send-email`, payload);
        const ok = resp?.data?.success ?? (resp?.status >= 200 && resp?.status < 300);
        if (ok) {
          // cerrar el modal inmediatamente y emitir events
          this.sending = false;
          this.$emit('send', { ticketId: id, to_user_id: this.to_user_id, subject: this.subject, body: this.message });
          // emitir también ticket-created para compatibilidad con el padre
          this.$emit('ticket-created', { ticketId: id, to_user_id: this.to_user_id, subject: this.subject, body: this.message });
          this.$emit('close');

          // recargar la lista en background (no bloquear el cierre)
          (async () => {
            try {
              if (this.showAllEmails) {
                await this.loadSentEmails(0);
              } else {
                const per = this.emailsPagination && this.emailsPagination.per_page ? this.emailsPagination.per_page : 5;
                await this.loadSentEmails(per, 1);
              }
            } catch (e) { /* ignore */ }
          })();
        } else {
          if (import.meta.env.DEV) console.error('Error enviando correo', resp?.data ?? resp);
          Swal.fire({ icon: 'error', title: 'Error', text: 'Error al enviar el correo.', showConfirmButton: false, timer: 2500 });
        }
      } catch (e) {
        if (import.meta.env.DEV) console.error('Exception sending email', e);
        const msg = e.response?.data?.message || 'Error al enviar el correo.';
        Swal.fire({ icon: 'error', title: 'Error', text: msg, showConfirmButton: false, timer: 3000 });
      } finally {
        this.sending = false;
      }
     },

    close() {
      // método llamado por el botón de cerrar en la plantilla
      this.$emit('close');
    },

    async loadSentEmails(perPage = 5, page = 1) {
      const id = this.effectiveTicketId;
      if (!id) return;
      this.loadingEmails = true;
      try {
        // construir query string según perPage y page
        const perPageParam = typeof perPage === 'number' ? `per_page=${perPage}` : '';
        const pageParam = page ? `&page=${page}` : '';
        const qs = perPageParam ? `?${perPageParam}${pageParam}` : (pageParam ? `?${pageParam.slice(1)}` : '');
        const resp = await api.get(`/tickets/${id}/emails${qs}`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` },
        });

        // Si la API devuelve paginación (Laravel paginator), los items están en resp.data.data
        const pag = resp?.data ?? {};
        const items = pag.data ?? (Array.isArray(resp?.data) ? resp.data : []);
        this.sentEmails = Array.isArray(items) ? items : [];

        // actualizar metadata de paginación si existe
        if (pag && typeof pag === 'object' && 'current_page' in pag) {
          this.emailsPagination.current_page = pag.current_page || 1;
          this.emailsPagination.last_page = pag.last_page || 1;
          this.emailsPagination.per_page = pag.per_page || perPage || 5;
          this.emailsPagination.total = pag.total || (Array.isArray(items) ? items.length : 0);
        } else {
          // si no hay paginación (per_page=0), ajustar meta para mostrar todo
          this.emailsPagination.current_page = 1;
          this.emailsPagination.last_page = 1;
          this.emailsPagination.per_page = Array.isArray(items) ? items.length : 0;
          this.emailsPagination.total = Array.isArray(items) ? items.length : 0;
        }
      } catch (err) {
        if (import.meta.env.DEV) console.error('Error cargando emails enviados:', err);
        this.sentEmails = [];
      } finally {
        this.loadingEmails = false;
      }
    },

    toggleEmailExpand(emailId) {
      this.expandedEmailIds[emailId] = !this.expandedEmailIds[emailId];
    },

    async toggleShowAllEmails() {
      if (!this.showAllEmails) {
        await this.loadSentEmails(0);
        this.showAllEmails = true;
      } else {
        await this.loadSentEmails(5, 1);
        this.showAllEmails = false;
      }
    },

    formatSentAt(sentAt) {
      return formatDateTime(sentAt);
    },

    previewBodyLines(body) {
      if (!body) return '';
      // tomar las primeras 2 líneas
      const lines = body.split(/\r?\n/).filter(Boolean);
      if (lines.length <= 2) return lines.join('\n');
      return lines.slice(0, 2).join('\n');
    },
    previewLines(body) {
      // devolver hasta 3 líneas para la vista previa
      if (!body) return ['', '', ''];
      const lines = body.split(/\r?\n/).filter(Boolean);
      return [lines[0] || '', lines[1] || '', lines[2] || ''];
    },
    hasMoreLines(body) {
      if (!body) return false;
      const lines = body.split(/\r?\n/).filter(Boolean);
      // si hay más de 3 líneas, hay más contenido
      if (lines.length > 3) return true;
      // considerar también textos largos sin saltos de línea (ajustar umbral)
      if (typeof body === 'string' && body.length > 360) return true;
      return false;
    },
  }
};
</script>

<style>
/* overlay teletransportado: cubrir todo y bloquear interacción */
.email-modal-teleport-wrapper {
  -webkit-tap-highlight-color: transparent;
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.80);
  z-index: 140000;
  pointer-events: auto;
}

/* Usar las mismas clases y estilos que el modal padre */
.modal-container,
.email-modal-container {
  position: relative;
  z-index: 140001;
  max-width: 900px;
  width: 94%;
  margin: 0 12px;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0,0,0,0.45);
  padding: 15px 20px; /* padding aplicado a todo el modal */
  box-sizing: border-box;
}

/* estructura interna */
.modal-content {
  display: flex;
  flex-direction: column;
  min-width: 280px;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  margin-top: -15px;           /* compensa el padding superior del contenedor */
  left: -20px;                 /* compensa el padding lateral del contenedor */
  width: calc(100% + 40px);    /* ocupa todo el ancho hasta los bordes */
  background: #99172A;
  color: #fff;
  padding: 12px 20px;
  border-radius: 8px 8px 0 0;
}
.modal-title {
  margin: 5px;;
  font-size: 1.5rem;
  line-height: 1;
  font-weight: 600;
  padding-top: 6px; /* separa el título del borde superior */
  padding-bottom: 2px;
  text-align: center;
}

/* botón cerrar: mover a la derecha, centrar verticalmente y quitar margen lateral */
.btn-close {
  position: absolute;
  right: 16px;                    /* alineado con el padding del header */
  top: 50%;
  transform: translateY(-50%);    /* centrado vertical */
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: 0;                /* eliminar desplazamiento previo */
  padding: 0;
}

/* icono material dentro del botón: asegurar blanco */
.btn-close .material-icons {
  font-size: 20px;
  line-height: 1;
  color: #fff !important;
  display: block;
}

.modal-body {
  padding: 14px 16px;
  background: transparent;
  color: #333;
  overflow: auto;
}

.form-group { margin-bottom: 10px; display:flex; flex-direction:column; }
.form-group label { margin-bottom:6px; font-weight:600; font-size:0.95rem; }
.form-group input,
.form-group textarea {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  font-size: 0.95rem;
  background: #fff;
  color: #333;
}

.modal-footer {
  display:flex;
  justify-content:flex-end;
  gap:8px;
  padding: 12px 16px;
  background: #fafafa;
}

.btn { padding: 8px 12px; border-radius: 4px; cursor: pointer; border: none; }
.btn-primary { background:#99172A; color:#fff; }

/* asegurar que el botón Enviar tenga exactamente el color de la franja superior */
.modal-footer .btn-primary {
  background: #99172A !important;
  color: #fff !important;
  border: none !important;
  box-shadow: none !important;
}

/* ligera variación al pasar el ratón / foco */
.modal-footer .btn-primary:hover,
.modal-footer .btn-primary:focus {
  background: #7a1220 !important;
  outline: none;
}

/* compatibilidad: si el padre utiliza .modal-container directo dentro del DOM teletransportado */
.email-modal-teleport-wrapper > .modal-container { display: block; }

/* estilos para cuerpo del email dentro de la lista: asegurar wrap y ajuste al ancho del contenedor */
.email-body { width: 100%; box-sizing: border-box; }
.email-body-preview, .email-body-full {
  white-space: pre-wrap; /* respetar saltos de línea y hacer wrap */
  word-break: break-word; /* partir palabras largas si es necesario */
  overflow-wrap: anywhere;
  max-width: 100%;
}
.email-body-full { margin-bottom: 6px; }
.email-toggle { color: #99172A; margin-left: 6px; cursor: pointer; text-decoration: underline; }

.preview-line { display: block; width: 100%; }
.preview-line-1 { margin-bottom: 4px; }
.preview-line-2 { display: flex; align-items: center; gap:6px; }
.preview-line-2-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: inline-block; flex: 1 1 auto; min-width: 0; }
.preview-line-2 .email-toggle { margin-left: 6px; white-space: nowrap; flex: 0 0 auto; color: #99172A; text-decoration: underline; }

.preview-line-single { display:flex; align-items:center; gap:6px; }
.preview-line-single-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display:inline-block; flex:1 1 auto; min-width:0; }
.preview-line-single .email-toggle { white-space: nowrap; flex:0 0 auto; color:#99172A; text-decoration: underline; margin-left:6px; }

/* reglas para la tercera línea cuando mostramos hasta 3 filas en la vista previa */
.preview-line-3 { display:flex; align-items:center; gap:6px; }
.preview-line-3-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display:inline-block; flex:1 1 auto; min-width:0; }
.preview-line-3 .email-toggle { white-space: nowrap; flex:0 0 auto; color:#99172A; text-decoration: underline; margin-left:6px; }

/* clamping a 2 líneas para la vista previa del email */
/* clamping a 3 líneas para la vista previa del email (fallback si se usa clamping) */
.email-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap; /* respetar saltos de línea */
  word-break: break-word;
}

/* alternar colores entre filas de emails para mejor legibilidad */


</style>